export const M10_UNIT_15 = {
  id: "m10-u15-wish-regrets",
  title: "Unit 15: Wishes & Regrets",
  topic: "Wish + Past Simple · Wish + Past Perfect · If Only · Wish vs Hope",
  iconType: "heart",
  grammarImage: "/m10-u15-wish-regrets.jpg",
  grammarText: "Unit focus: The verb 'wish' for present unreal desires (wish + past simple) and past regrets (wish + past perfect). Also: wish + could for impossible abilities, if only for emphasis, and the key contrast between wish (unreal) and hope (still possible). The final unit of the app — six characters, six lives, six wishes.",

  sets: [

    // ===================================================================
    // SET 1: WISH + PAST SIMPLE — PRESENT UNREAL (B1+)
    // ===================================================================
    {
      id: "m10-u15-set1",
      title: "Set 1: I Want Things to Be Different",
      context: `*London — a creative writing workshop, Saturday morning*

Six strangers had signed up for a one-day writing workshop.
The theme was simple: wishes and regrets.
The first exercise was even simpler.
"Write one sentence," said the facilitator.
"Something about your life right now that you wish were different."
The room went quiet. Everyone had something.

*Londres — un taller de escritura creativa, sábado por la mañana*

Seis desconocidos se habían apuntado a un taller de escritura de un día.
El tema era sencillo: deseos y lamentos.
El primer ejercicio era aún más sencillo.
"Escribid una frase," dijo el facilitador.
"Algo de vuestra vida ahora mismo que desearíais que fuera diferente."
La sala quedó en silencio. Todos tenían algo.`,

      questions: [
        {
          type: "match",
          q: "Match the wish + past simple rules:",
          pairs: [
            { left: "wish + past simple", right: "deseo sobre el PRESENTE — la realidad es lo contrario" },
            { left: "I wish I lived nearby.", right: "Reality: I don't live nearby. (pero no es así)" },
            { left: "I wish I weren't too young.", right: "Reality: I am too young. (pero lo soy)" }
          ],
          a: "completed",
          successTip: "WISH + PAST SIMPLE = present unreal! The wish is the OPPOSITE of reality. 'I wish I lived nearby' = I DON'T live nearby. The past simple doesn't mean past time — it means unreal! 💭"
        },

        {
          type: "match",
          q: "Match the vocabulary to its meaning:",
          pairs: [
            { left: "live far away", right: "vivir lejos (de familia, amigos, hogar)" },
            { left: "marry", right: "casarse (con alguien)" },
            { left: "too young", right: "demasiado joven (para algo)" }
          ],
          a: "completed",
          successTip: "LIVE FAR AWAY (the distance that separates), MARRY (the commitment), TOO YOUNG (the feeling of not being ready). Three life themes — all appear in wish sentences! 💍🌍👶"
        },

        {
          type: "mc",
          q: "Nadia's reality: 'I live far away from my family.' Her wish:",
          options: [
            "I wish I didn't live so far away from my family.",
            "I wish I don't live so far away from my family.",
            "I wish I hadn't lived so far away from my family."
          ],
          a: "I wish I didn't live so far away from my family.",
          tip: "Current situation → wish + past simple (didn't live). NOT present (don't) — wish always takes past. NOT past perfect (hadn't) — that's for past regrets!",
          successTip: "DIDN'T LIVE ✅ (past simple for present wish). DON'T ❌ (never present after wish!). HADN'T LIVED ❌ (that's past perfect — only for past regrets!). LIVE FAR AWAY vocabulary! 🌍"
        },

        {
          type: "mc",
          q: "Lena's reality: 'I'm too young to know what I want.' Her wish:",
          options: [
            "She wishes she weren't too young.",
            "She wishes she wasn't too young.",
            "She wishes she isn't too young."
          ],
          a: "She wishes she weren't too young.",
          tip: "Were (formal) / was (informal) — both acceptable after wish! Were is formally correct for all persons. Isn't ❌ (never present after wish!).",
          successTip: "WEREN'T (formal) or WASN'T (informal) — both correct after wish! WERE is formally preferred for all persons (I/she/he/it were). ISN'T ❌ — never present tense after wish! TOO YOUNG vocabulary! 👶"
        },

        {
          type: "mc",
          q: "Marcus's reality: 'I'm not married.' His wish:",
          options: [
            "He wishes he were married.",
            "He wishes he was married.",
            "Both A and B are correct."
          ],
          a: "Both A and B are correct.",
          tip: "Were (formal/written) and was (informal/spoken) are both acceptable after wish for he/she/it.",
          successTip: "BOTH CORRECT! WERE (formal — preferred in writing and grammar rules) and WAS (informal — common in everyday speech). Both are accepted. MARRY vocabulary! 💍"
        },

        {
          type: "mc",
          q: "Priya's reality: 'My husband lives far away for work.' Her wish for the present situation:",
          options: [
            "She wishes her husband didn't live so far away.",
            "She wishes her husband hadn't lived so far away.",
            "She wishes her husband doesn't live so far away."
          ],
          a: "She wishes her husband didn't live so far away.",
          tip: "Current situation → wish + past simple (didn't live). Hadn't lived = past perfect (for past regret — wrong here!). Doesn't ❌ (never present!)",
          successTip: "DIDN'T LIVE ✅ (current situation = past simple). HADN'T LIVED ❌ (that's for a completed past event). The situation is still happening NOW — use past simple! LIVE FAR AWAY! 🌍"
        },

        {
          type: "mc",
          q: "Lena writes in her notebook: '___ I ___ older — and ___ I ___ what I wanted from life.'",
          options: ["I wish / were / I wish / knew", "I wish / was / I hope / know", "If only / am / I wish / know"],
          a: "I wish / were / I wish / knew",
          tip: "Wish + were (formal past simple for present unreal). Wish + knew (past simple of know — present unreal).",
          successTip: "I WISH I WERE OLDER + I WISH I KNEW — both wish + past simple for present unreal situations. WERE (formal, all persons) + KNEW (past of know). TOO YOUNG theme! 👶"
        },

        {
          type: "mc",
          q: "Which sentence expresses a PRESENT wish (not a past regret)?",
          options: [
            "I wish I lived closer to my family.",
            "I wish I had lived closer to my family.",
            "I wish I had married earlier."
          ],
          a: "I wish I lived closer to my family.",
          tip: "Past simple (lived) = present wish — I don't live close NOW. Past perfect (had lived / had married) = past regret.",
          successTip: "LIVED (past simple) = PRESENT wish — the situation is CURRENT. HAD LIVED / HAD MARRIED (past perfect) = PAST regret — the event is OVER. The tense after wish tells you the time! 🔑"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá no viviera tan lejos — y ojalá pudiera visitar a mi familia cada mes.'",
          words: ["I", "wish", "I", "didn't", "live", "so", "far", "away", "—", "and", "I", "wish", "I", "could", "visit", "my", "family", "every", "month."],
          a: "I wish I didn't live so far away — and I wish I could visit my family every month.",
          extra: ["don't", "haven't", "lived"]
        },

        {
          type: "input",
          q: "Marcus writes his first sentence: '___ ___ ___ married — and ___ ___ ___ so far away from everything that matters.' (I wish + were / didn't live)",
          a: "I / wish / I / were / I / wish / I",
          alts: ["I wish I were / I wish I didn't"],
          successTip: "I WISH I WERE MARRIED + I WISH I DIDN'T LIVE FAR AWAY — two present wishes, past simple both times. MARRY + LIVE FAR AWAY vocabulary! 💍🌍✅"
        }
      ]
    },

    // ===================================================================
    // SET 2: WISH + PAST PERFECT — PAST REGRET (B1+)
    // ===================================================================
    {
      id: "m10-u15-set2",
      title: "Set 2: I Can't Change What Happened",
      context: `*The workshop — second exercise*

"Now," said the facilitator, "write about something in your past
that you wish had been different.
Something you did — or didn't do — that you can't go back and change."
The room went even quieter.
These sentences were harder.
These were the ones that really meant something.

*El taller — segundo ejercicio*

"Ahora," dijo el facilitador, "escribid sobre algo en vuestro pasado
que desearíais que hubiera sido diferente.
Algo que hicisteis — o no hicisteis — que no podéis cambiar."
La sala quedó aún más en silencio.
Estas frases eran más difíciles.
Estas eran las que realmente significaban algo.`,

      questions: [
        {
          type: "match",
          q: "Match the wish + past perfect rules:",
          pairs: [
            { left: "wish + past perfect", right: "lamento sobre el PASADO — ya ocurrió, no se puede cambiar" },
            { left: "I wish I hadn't dropped out.", right: "Reality: I dropped out. (ya pasó, no puedo cambiarlo)" },
            { left: "I wish I had studied art.", right: "Reality: I didn't study art. (ya pasó, no puedo cambiarlo)" }
          ],
          a: "completed",
          successTip: "WISH + PAST PERFECT = past regret! The event ALREADY HAPPENED and CANNOT be changed. 'I wish I hadn't dropped out' = I DID drop out (past fact). Had + past participle always! 💭⏪"
        },

        {
          type: "match",
          q: "Match the vocabulary to its meaning:",
          pairs: [
            { left: "drop out of school", right: "abandonar la escuela / los estudios antes de terminar" },
            { left: "give up", right: "rendirse / dejar algo que empezaste" },
            { left: "study art", right: "estudiar arte (carrera, asignatura, disciplina)" }
          ],
          a: "completed",
          successTip: "DROP OUT OF SCHOOL (leave before finishing), GIVE UP (abandon something you started), STUDY ART (follow a creative path). Three life decisions — all possible regrets! 🎨📚"
        },

        {
          type: "mc",
          q: "Elena's reality: 'I dropped out of my art degree at 20.' Her regret:",
          options: [
            "She wishes she hadn't dropped out of her art degree.",
            "She wishes she didn't drop out of her art degree.",
            "She wishes she doesn't drop out of her art degree."
          ],
          a: "She wishes she hadn't dropped out of her art degree.",
          tip: "Past event (dropped out) → wish + past perfect (hadn't dropped out). Didn't = past simple (wrong — that's for present wish). Doesn't = never after wish!",
          successTip: "HADN'T DROPPED OUT ✅ (past perfect — past regret!). DIDN'T DROP OUT ❌ (past simple — that would mean a PRESENT wish, not a past regret!). The tense tells the time! DROP OUT vocabulary! 🎨"
        },

        {
          type: "mc",
          q: "Dani's reality: 'I gave up my first engineering project.' His regret:",
          options: [
            "He wishes he hadn't given up.",
            "He wishes he didn't give up.",
            "He wishes he hasn't given up."
          ],
          a: "He wishes he hadn't given up.",
          tip: "Past event → wish + past perfect (hadn't given up). Give up → given up (irregular past participle). Didn't ❌ (present wish). Hasn't ❌ (never present after wish!)",
          successTip: "HADN'T GIVEN UP ✅ — GIVE → GAVE → GIVEN (irregular!). Past event = past perfect after wish. GIVE UP vocabulary! 📚"
        },

        {
          type: "mc",
          q: "Marcus's reality: 'I didn't study art — I studied finance.' His regret:",
          options: [
            "He wishes he had studied art.",
            "He wishes he studied art.",
            "He wishes he studies art."
          ],
          a: "He wishes he had studied art.",
          tip: "He didn't study art (past event) → wish + past perfect (had studied). Studied = past simple (present wish — wrong here). Studies ❌ (never present!)",
          successTip: "HAD STUDIED ART ✅ — past event = past perfect! STUDIED ❌ = that would mean a PRESENT wish ('I wish I studied art' = I don't study art NOW). The difference is crucial! 🎨"
        },

        {
          type: "mc",
          q: "Choose the sentence with a PAST regret (not a present wish):",
          options: [
            "I wish I had studied art instead of finance.",
            "I wish I studied art right now.",
            "I wish I study art every day."
          ],
          a: "I wish I had studied art instead of finance.",
          tip: "Had studied = past perfect = past regret (can't change). Studied = past simple = present wish. Studies ❌",
          successTip: "HAD STUDIED (past perfect) = past regret — the choice was made, cannot be undone. STUDIED (past simple) = present wish — still possible to change NOW. The tense is everything! 🔑"
        },

        {
          type: "mc",
          q: "Priya writes: '___ I ___ so young — and ___ I ___ ___ out of university.' (wish + married + hadn't dropped)",
          options: ["I wish / hadn't married / I wish / hadn't dropped", "I wish / didn't marry / I wish / didn't drop", "If only / married / I hope / hadn't dropped"],
          a: "I wish / hadn't married / I wish / hadn't dropped",
          tip: "Both past events = both wish + past perfect. Hadn't married (married is regular: marry → married). Hadn't dropped out.",
          successTip: "HADN'T MARRIED + HADN'T DROPPED OUT — both past perfect for past regrets! Two events in the past that can't be changed. MARRY + DROP OUT vocabulary! 💍📚"
        },

        {
          type: "mc",
          q: "Lena (19) has no regrets yet — but she worries. She says: 'I ___ ___ ___ up on my dreams before I even start.'",
          options: ["wish / I / don't give", "hope / I / don't give", "wish / I / hadn't given"],
          a: "hope / I / don't give",
          tip: "Still possible (future concern) = hope (not wish). Hope + present/future. Wish + hadn't given = past regret (she hasn't given up yet!)",
          successTip: "HOPE I DON'T GIVE UP ✅ — HOPE (still possible!) + present tense. WISH I HADN'T GIVEN UP ❌ = she already gave up (but she hasn't!). WISH vs HOPE distinction previewed! 🌟"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá hubiera estudiado arte — y ojalá no me hubiera rendido cuando las cosas se pusieron difíciles.'",
          words: ["I", "wish", "I", "had", "studied", "art", "—", "and", "I", "wish", "I", "hadn't", "given", "up", "when", "things", "got", "difficult."],
          a: "I wish I had studied art — and I wish I hadn't given up when things got difficult.",
          extra: ["have", "gave", "studied"]
        },

        {
          type: "input",
          q: "Elena's workshop sentence: '___ ___ ___ ___ ___ of school — because if I hadn't, I ___ ___ art for the last twelve years.' (I wish + hadn't dropped out / would have studied)",
          a: "I / wish / I / hadn't / dropped / would have studied",
          alts: ["I wish I hadn't dropped out of school / would have studied"],
          successTip: "I WISH I HADN'T DROPPED OUT — past perfect regret! + WOULD HAVE STUDIED (third conditional consequence). The regret and its consequence in one sentence. DROP OUT + STUDY ART! 🎨✅"
        }
      ]
    },

    // ===================================================================
    // SET 3: WISH + PAST SIMPLE NEGATIVE (B2-)
    // ===================================================================
    {
      id: "m10-u15-set3",
      title: "Set 3: Wishing Things Away",
      context: `*The workshop — third exercise*

"Sometimes a wish isn't about wanting something you don't have,"
said the facilitator. "Sometimes it's about wanting something
you DO have — to go away."
A wish about a reality you want to undo.
A wish with a negative.

*El taller — tercer ejercicio*

"A veces un deseo no es sobre querer algo que no tienes,"
dijo el facilitador. "A veces es sobre querer que algo
que SÍ tienes — desaparezca."
Un deseo sobre una realidad que quieres deshacer.
Un deseo con un negativo.`,

      questions: [
        {
          type: "match",
          q: "Match the negative wish structures:",
          pairs: [
            { left: "I wish I didn't + verb", right: "quiero que algo que hago ahora DEJE de ocurrir" },
            { left: "I wish I weren't + adj/noun", right: "quiero que algo que SOY ahora sea diferente" },
            { left: "She wishes she didn't live far away.", right: "Reality: She lives far away. (y no quiere)" }
          ],
          a: "completed",
          successTip: "Negative wish + past simple = 'I want this CURRENT reality to STOP.' I WISH I DIDN'T (do something) = I DO it and I don't want to. I WISH I WEREN'T = I AM this and I don't want to be! 💭"
        },

        {
          type: "mc",
          q: "Lena's reality: 'I feel too young for everything here.' Her wish:",
          options: [
            "She wishes she didn't feel so young.",
            "She wishes she hadn't felt so young.",
            "She wishes she doesn't feel so young."
          ],
          a: "She wishes she didn't feel so young.",
          tip: "Current feeling → wish + past simple negative (didn't feel). Hadn't felt = past perfect (for past regret). Doesn't ❌",
          successTip: "DIDN'T FEEL SO YOUNG ✅ — current situation, negative wish, past simple. TOO YOUNG theme! HADN'T FELT ❌ = that would mean she no longer feels young now (past regret). 👶"
        },

        {
          type: "mc",
          q: "Marcus's reality: 'I study financial reports all day.' His wish:",
          options: [
            "He wishes he didn't study financial reports all day.",
            "He wishes he hadn't studied financial reports all day.",
            "He wishes he studied art instead."
          ],
          a: "He wishes he didn't study financial reports all day.",
          tip: "Current daily reality → wish + past simple negative (didn't study). Hadn't studied = past perfect (past regret). 'He wishes he studied art' = present wish — also valid but not the negative form!",
          successTip: "DIDN'T STUDY ✅ — negative present wish (he DOES study reports and wants to STOP). HADN'T STUDIED ❌ = past regret (the studying is over). STUDY vocabulary in context! 🎨"
        },

        {
          type: "mc",
          q: "Nadia's reality: 'I live far away from Morocco.' Her current wish:",
          options: [
            "She wishes she didn't live so far away from Morocco.",
            "She wishes she hadn't lived so far away from Morocco.",
            "She wishes she lived closer to Morocco."
          ],
          a: "She wishes she didn't live so far away from Morocco.",
          tip: "Both A and C express a present wish — A is the negative form (wishing the distance away). C is the positive form (wishing for something she doesn't have). Both are grammatically correct — A is the negative version.",
          successTip: "DIDN'T LIVE FAR AWAY (negative — wishing the distance AWAY) vs LIVED CLOSER (positive — wishing for nearness). Both correct present wishes! Different angles of the same reality. LIVE FAR AWAY! 🌍"
        },

        {
          type: "mc",
          q: "Elena's reality: 'I feel guilty about dropping out.' Her current feeling wish:",
          options: [
            "She wishes she didn't feel so guilty.",
            "She wishes she hadn't felt so guilty.",
            "She wishes she doesn't feel so guilty."
          ],
          a: "She wishes she didn't feel so guilty.",
          tip: "Current emotion → wish + past simple negative. Hadn't felt = past regret (would mean the guilt is gone — it isn't!)",
          successTip: "DIDN'T FEEL GUILTY ✅ — present negative wish (she STILL feels guilty NOW). HADN'T FELT ❌ = the guilt happened in the past and is over. She still carries it! DROP OUT theme! 🎨"
        },

        {
          type: "mc",
          q: "Priya's reality: 'I'm married to someone I don't love anymore.' Her wish:",
          options: [
            "She wishes she weren't married to him.",
            "She wishes she wasn't married to him.",
            "Both A and B are correct.",
          ],
          a: "Both A and B are correct.",
          tip: "Weren't (formal) and wasn't (informal) are both correct after wish for he/she/it.",
          successTip: "WEREN'T (formal) and WASN'T (informal) — both correct! WEREN'T is the grammatically formal form (subjunctive), WASN'T is more common in everyday speech. MARRY vocabulary! 💍"
        },

        {
          type: "mc",
          q: "Dani's reality: 'I give up too easily.' His self-critical wish:",
          options: [
            "He wishes he didn't give up so easily.",
            "He wishes he hadn't given up so easily.",
            "He wishes he gave up less."
          ],
          a: "He wishes he didn't give up so easily.",
          tip: "Current habit/tendency → wish + past simple negative. Hadn't given up = specific past event. Gave up less = also grammatically possible present wish!",
          successTip: "DIDN'T GIVE UP SO EASILY ✅ — negative wish about a CURRENT habit (he does this regularly). HADN'T GIVEN UP ❌ (that's a specific past event). GIVE UP vocabulary! 📚"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá no me sintiera tan joven — y ojalá no viviera tan lejos de las personas que me importan.'",
          words: ["I", "wish", "I", "didn't", "feel", "so", "young", "—", "and", "I", "wish", "I", "didn't", "live", "so", "far", "away", "from", "the", "people", "who", "matter", "to", "me."],
          a: "I wish I didn't feel so young — and I wish I didn't live so far away from the people who matter to me.",
          extra: ["don't", "hadn't", "felt"]
        },

        {
          type: "mc",
          q: "The facilitator asks the group: 'Who wishes they ___ something they currently do?' Lena answers: 'I wish I ___ studying something I don't believe in.'",
          options: ["didn't do / weren't", "hadn't done / hadn't been", "don't do / am not"],
          a: "didn't do / weren't",
          tip: "Current situation = wish + past simple negative. Didn't do + weren't (past simple negative of be). NEVER present tense after wish!",
          successTip: "DIDN'T DO + WEREN'T (both past simple negative) — current wishes about current realities. STUDY ART theme (studying something she doesn't believe in vs art!). 🎨👶"
        },

        {
          type: "input",
          q: "Marcus's honest sentence: 'I wish I ___ work in finance — and I wish I ___ so far away from who I really am.' (didn't + live — present negative wishes)",
          a: "didn't / didn't live",
          alts: ["didn't work / didn't live"],
          successTip: "DIDN'T WORK IN FINANCE + DIDN'T LIVE FAR AWAY — two negative present wishes. Both past simple. Both express current realities he wants to change. LIVE FAR AWAY vocabulary! 🌍✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: WISH + PAST PERFECT NEGATIVE (B2-)
    // ===================================================================
    {
      id: "m10-u15-set4",
      title: "Set 4: I Wish I Hadn't",
      context: `*The workshop — fourth exercise*

"The hardest wishes," said the facilitator,
"are the ones about things you did.
Not the things you missed — but the things you chose.
The ones that felt right at the time.
The ones you wish you could undo."
Every pen in the room moved slowly.

*El taller — cuarto ejercicio*

"Los deseos más difíciles," dijo el facilitador,
"son los que tratan de cosas que hicisteis.
No las que os perdisteis — sino las que elegisteis.
Las que parecían correctas en su momento.
Las que desearíais poder deshacer."
Todos los bolígrafos en la sala se movieron despacio.`,

      questions: [
        {
          type: "match",
          q: "Match the negative past perfect wish structures:",
          pairs: [
            { left: "I wish I hadn't + past participle", right: "lamento haber HECHO algo en el pasado" },
            { left: "She wishes she hadn't married.", right: "Reality: She married. (y lo lamenta)" },
            { left: "He wishes he hadn't given up.", right: "Reality: He gave up. (y lo lamenta)" }
          ],
          a: "completed",
          successTip: "WISH + HADN'T + PAST PARTICIPLE = regret about something you DID. The action happened — you wish it hadn't. The negative tells you the person DID the thing but wishes they hadn't! ⏪💭"
        },

        {
          type: "mc",
          q: "Priya's regret: 'I married at 22.' Her wish:",
          options: [
            "She wishes she hadn't married so young.",
            "She wishes she didn't marry so young.",
            "She wishes she wasn't married so young."
          ],
          a: "She wishes she hadn't married so young.",
          tip: "Past completed action → wish + past perfect negative (hadn't married). Didn't = present wish (she's still getting married NOW?!). Wasn't = wrong form.",
          successTip: "HADN'T MARRIED ✅ — past completed action + regret. DIDN'T MARRY ❌ = present wish (she's still doing it NOW, which doesn't make sense!). MARRY vocabulary! 💍⏪"
        },

        {
          type: "mc",
          q: "Dani's regret: 'I gave up my engineering project in 2019.' His wish:",
          options: [
            "He wishes he hadn't given up his project.",
            "He wishes he didn't give up his project.",
            "He wishes he gives up less."
          ],
          a: "He wishes he hadn't given up his project.",
          tip: "Specific past event (2019) → wish + past perfect negative. Give up → gave up → given up (irregular).",
          successTip: "HADN'T GIVEN UP ✅ — specific past event = past perfect. GIVE → GAVE → GIVEN (irregular participle!). GIVE UP vocabulary! 📚⏪"
        },

        {
          type: "mc",
          q: "Elena's regret: 'I dropped out of school at 20.' Her wish:",
          options: [
            "She wishes she hadn't dropped out of school.",
            "She wishes she didn't drop out of school.",
            "She wishes she hasn't dropped out of school."
          ],
          a: "She wishes she hadn't dropped out of school.",
          tip: "Past event → wish + past perfect negative (hadn't dropped out). Didn't = present wish. Hasn't ❌ (never present!)",
          successTip: "HADN'T DROPPED OUT ✅ — past event + regret. DROP OUT OF SCHOOL vocabulary! Dropped out → hadn't dropped out (the wish reverses the reality). ⏪🎨"
        },

        {
          type: "mc",
          q: "Marcus's regret: 'I told everyone art was a waste of time.' His wish:",
          options: [
            "He wishes he hadn't said that art was a waste of time.",
            "He wishes he didn't say that art was a waste of time.",
            "He wishes he hasn't said that art was a waste of time."
          ],
          a: "He wishes he hadn't said that art was a waste of time.",
          tip: "Past statement → wish + past perfect negative (hadn't said). Didn't = present wish. Hasn't ❌",
          successTip: "HADN'T SAID ✅ — past statement he regrets. The regret connects to STUDY ART theme (he dismissed art, now wishes he hadn't!). Past perfect for past regret! 🎨⏪"
        },

        {
          type: "mc",
          q: "Lena (19) hasn't done much yet — but she has one regret: 'I gave up drawing when I was 14.' Her wish:",
          options: [
            "She wishes she hadn't given up drawing.",
            "She wishes she didn't give up drawing.",
            "She wishes she hadn't dropped out of art class."
          ],
          a: "She wishes she hadn't given up drawing.",
          tip: "Past event (at 14) → wish + past perfect negative. Hadn't given up = correct. Didn't give up = present wish (wrong — she's already 19!)",
          successTip: "HADN't GIVEN UP ✅ — past event at 14, now she regrets it at 19. GIVE UP vocabulary! This connects to her age (TOO YOUNG — she gave up before she was ready to decide!) 👶🎨"
        },

        {
          type: "mc",
          q: "Nadia reflects: 'I wish I ___ my old friends when I moved abroad — and I wish I ___ in touch more.' ",
          options: ["hadn't lost / had kept", "didn't lose / kept", "hadn't lose / had keep"],
          a: "hadn't lost / had kept",
          tip: "Past actions (moving abroad, losing touch) = past perfect. Hadn't lost + had kept = past perfect pair. Lost and kept = irregular past participles.",
          successTip: "HADN'T LOST + HAD KEPT — two past perfect wishes (regrets about the past). LOSE → LOST → LOST and KEEP → KEPT → KEPT (irregular!). LIVE FAR AWAY theme! 🌍⏪"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá no me hubiera rendido — y ojalá no me hubiera casado tan joven. Ahora sería una persona completamente diferente.'",
          words: ["I", "wish", "I", "hadn't", "given", "up", "—", "and", "I", "wish", "I", "hadn't", "married", "so", "young.", "I", "would", "be", "a", "completely", "different", "person", "now."],
          a: "I wish I hadn't given up — and I wish I hadn't married so young. I would be a completely different person now.",
          extra: ["have", "gave", "married"]
        },

        {
          type: "mc",
          q: "Spot the error: 'She wishes she hadn't dropped out of school and she wishes she didn't gave up her dreams.'",
          options: [
            "'Didn't gave up' is wrong — should be 'hadn't given up' (past perfect) or 'didn't give up' (past simple, no -ed).",
            "'Hadn't dropped out' is wrong — should be 'didn't drop out'.",
            "No errors — both sentences are correct."
          ],
          a: "'Didn't gave up' is wrong — should be 'hadn't given up' (past perfect) or 'didn't give up' (past simple, no -ed).",
          tip: "DIDN'T GAVE = double past error! After didn't, use base form (didn't give). OR use past perfect: hadn't given up. Both didn't + base or hadn't + past participle!",
          successTip: "DIDN'T GAVE ❌ — two errors in one! After DIDN'T, always use BASE FORM (didn't give ✅). OR use PAST PERFECT: hadn't given up ✅. Never DIDN'T + past form! GIVE UP vocabulary! 📚"
        },

        {
          type: "input",
          q: "Priya's most honest sentence: '___ ___ ___ ___ so young — if I hadn't, I ___ ___ art and become someone different.' (I wish + hadn't married / would have studied)",
          a: "I / wish / I / hadn't / married / would have studied",
          alts: ["I wish I hadn't married so young / would have studied"],
          successTip: "I WISH I HADN'T MARRIED SO YOUNG + WOULD HAVE STUDIED ART — past regret + third conditional consequence. MARRY + STUDY ART vocabulary! The connection between two lives not lived! 💍🎨✅"
        }
      ]
    },

    // ===================================================================
    // SET 5: CONTRAST — PAST SIMPLE vs PAST PERFECT (B2)
    // ===================================================================
    {
      id: "m10-u15-set5",
      title: "Set 5: Now or Then?",
      context: `*The workshop — the facilitator explains*

"Some wishes are about right now," said the facilitator.
"The job you have. The city you live in. The person you are today.
These can still change — which is why the feeling is different.
Other wishes are about the past. Things that are done.
Finished. Sealed. The feeling is heavier.
Can you tell the difference in your own sentences?"

*El taller — el facilitador explica*

"Algunos deseos son sobre ahora mismo," dijo el facilitador.
"El trabajo que tienes. La ciudad donde vives. La persona que eres hoy.
Estas cosas aún pueden cambiar — por eso el sentimiento es diferente.
Otros deseos son sobre el pasado. Cosas que ya están hechas.
Terminadas. Selladas. El sentimiento es más pesado.
¿Podéis distinguir la diferencia en vuestras propias frases?"`,

      questions: [
        {
          type: "match",
          q: "Match the contrast rules:",
          pairs: [
            { left: "Wish + Past Simple", right: "PRESENTE irreal — la situación PUEDE cambiar todavía" },
            { left: "Wish + Past Perfect", right: "PASADO irreal — ya ocurrió, NO puede cambiar" },
            { left: "I wish I lived closer. / I wish I had lived closer.",
              right: "Presente: I don't live close NOW. / Pasado: I didn't live close THEN (and it's over)." }
          ],
          a: "completed",
          successTip: "The KEY contrast! Past Simple = CURRENT situation (can still change). Past Perfect = PAST event (cannot change). 'I wish I lived closer' = I can still move. 'I wish I had lived closer' = I didn't, and that time is gone. 🔑"
        },

        {
          type: "mc",
          q: "Elena currently works in tech but dreams of art. She also regrets leaving art school. Match each wish to the correct form:",
          options: [
            "Current job: 'I wish I worked in art.' / Past regret: 'I wish I hadn't dropped out.'",
            "Current job: 'I wish I had worked in art.' / Past regret: 'I wish I didn't drop out.'",
            "Current job: 'I wish I work in art.' / Past regret: 'I wish I hadn't dropped out.'"
          ],
          a: "Current job: 'I wish I worked in art.' / Past regret: 'I wish I hadn't dropped out.'",
          tip: "Current job = wish + past simple (worked). Past event = wish + past perfect (hadn't dropped out). Two different time frames, two different structures.",
          successTip: "WORKED (past simple — current wish, she can still change career!) + HADN'T DROPPED OUT (past perfect — past regret, can't undo leaving school!). Two structures, two time frames! 🎨🔑"
        },

        {
          type: "mc",
          q: "Priya is currently married AND got married young. She has two wishes. Which pair is correct?",
          options: [
            "Currently married: 'I wish I weren't married.' / Got married young: 'I wish I hadn't married so young.'",
            "Currently married: 'I wish I hadn't been married.' / Got married young: 'I wish I didn't marry so young.'",
            "Currently married: 'I wish I wasn't married.' / Got married young: 'I wish I didn't marry so young.'"
          ],
          a: "Currently married: 'I wish I weren't married.' / Got married young: 'I wish I hadn't married so young.'",
          tip: "Currently married (present situation) = weren't (past simple of be). Got married young (past event) = hadn't married (past perfect).",
          successTip: "WEREN'T MARRIED (present — she's still married NOW, can still change!) + HADN'T MARRIED SO YOUNG (past — the wedding happened, can't undo!). Same topic, two time frames! 💍🔑"
        },

        {
          type: "mc",
          q: "Dani currently gives up easily AND gave up a specific project in 2019. Two wishes:",
          options: [
            "'I wish I didn't give up so easily.' (habit) / 'I wish I hadn't given up that project.' (past event)",
            "'I wish I hadn't given up so easily.' (habit) / 'I wish I didn't give up that project.' (past event)",
            "'I wish I gave up less.' (habit) / 'I wish I hadn't given up.' (past event)"
          ],
          a: "'I wish I didn't give up so easily.' (habit) / 'I wish I hadn't given up that project.' (past event)",
          tip: "Habit (current) = past simple negative (didn't give up). Specific past event = past perfect (hadn't given up).",
          successTip: "DIDN'T GIVE UP (present habit — he still does this!) + HADN'T GIVEN UP (past event — 2019 project, can't undo!). Habit vs specific event = past simple vs past perfect! GIVE UP! 📚🔑"
        },

        {
          type: "mc",
          q: "Nadia lives far away NOW and also moved far away TEN YEARS AGO. Two wishes:",
          options: [
            "'I wish I didn't live so far away.' / 'I wish I hadn't moved so far away.'",
            "'I wish I hadn't lived so far away.' / 'I wish I didn't move so far away.'",
            "'I wish I lived closer.' / 'I wish I had moved closer ten years ago.'"
          ],
          a: "'I wish I didn't live so far away.' / 'I wish I hadn't moved so far away.'",
          tip: "Currently living far = present wish (didn't live). Moving far away ten years ago = past regret (hadn't moved). Both collocations LIVE FAR AWAY in context.",
          successTip: "DIDN'T LIVE FAR AWAY (present — she's there NOW, could move back!) + HADN'T MOVED FAR AWAY (past — she moved years ago, can't undo that choice!). Two time frames! 🌍🔑"
        },

        {
          type: "mc",
          q: "Which sentence is AMBIGUOUS — it could be present wish OR past regret?",
          options: [
            "'I wish I had studied art.' — past regret only (had studied = past perfect).",
            "'I wish I studied art.' — ambiguous: could mean NOW or THEN.",
            "'I wish I hadn't dropped out.' — past regret only."
          ],
          a: "'I wish I had studied art.' — past regret only (had studied = past perfect).",
          tip: "Had studied = clearly past perfect = past regret. Studied = past simple = PRESENT wish only (I don't study art NOW). Past perfect is unambiguous!",
          successTip: "HAD STUDIED = clearly past perfect = past regret (unambiguous!). STUDIED = past simple = present wish (I don't study NOW). The distinction is always clear when you know the rule! 🎨🔑"
        },

        {
          type: "mc",
          q: "Marcus has THREE situations. Match each to the correct wish form: A) He currently works in finance. B) He chose finance over art at 18. C) He tends to give up creative projects.",
          options: [
            "A: 'I wish I didn't work in finance.' / B: 'I wish I hadn't chosen finance.' / C: 'I wish I didn't give up creative projects.'",
            "A: 'I wish I hadn't worked in finance.' / B: 'I wish I didn't choose finance.' / C: 'I wish I hadn't given up.'",
            "A: 'I wish I worked in art.' / B: 'I wish I had studied art.' / C: 'I wish I didn't give up.'"
          ],
          a: "A: 'I wish I didn't work in finance.' / B: 'I wish I hadn't chosen finance.' / C: 'I wish I didn't give up creative projects.'",
          tip: "A = current job (past simple negative). B = past choice at 18 (past perfect). C = current habit (past simple negative).",
          successTip: "THREE situations, THREE structures! A (current) = DIDN'T WORK. B (past decision) = HADN'T CHOSEN. C (current habit) = DIDN'T GIVE UP. Context determines structure! 🎨📚🔑"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá no viviera tan lejos AHORA — y ojalá no me hubiera mudado tan lejos hace diez años.'",
          words: ["I", "wish", "I", "didn't", "live", "so", "far", "away", "now", "—", "and", "I", "wish", "I", "hadn't", "moved", "so", "far", "away", "ten", "years", "ago."],
          a: "I wish I didn't live so far away now — and I wish I hadn't moved so far away ten years ago.",
          extra: ["haven't", "don't", "had"]
        },

        {
          type: "input",
          q: "The facilitator's summary: 'Past simple after wish = ___ wish. Past perfect after wish = ___ regret. One can still change. One ___.' (present / past / cannot)",
          a: "present / past / cannot",
          alts: ["Present / past / can't change"],
          successTip: "PRESENT wish (past simple) = can still change. PAST regret (past perfect) = cannot change. That's the emotional weight behind the grammar! 🔑💭✅"
        }
      ]
    },

    // ===================================================================
    // SET 6: WISH + COULD (B2)
    // ===================================================================
    {
      id: "m10-u15-set6",
      title: "Set 6: Wishing for the Impossible",
      context: `*The workshop — afternoon session*

"There's a third kind of wish," said the facilitator.
"Not about what you have or don't have.
Not about what you did or didn't do.
About what you CAN'T do.
The abilities you don't have.
The possibilities that are closed.
Wishes that begin with: I wish I could."

*El taller — sesión de tarde*

"Hay un tercer tipo de deseo," dijo el facilitador.
"No sobre lo que tienes o no tienes.
No sobre lo que hiciste o no hiciste.
Sobre lo que NO PUEDES hacer.
Las habilidades que no tienes.
Las posibilidades que están cerradas.
Deseos que empiezan con: Ojalá pudiera."`,

      questions: [
        {
          type: "match",
          q: "Match the wish + could rules:",
          pairs: [
            { left: "wish + could", right: "habilidad o posibilidad irreal en el presente" },
            { left: "I wish I could study art.", right: "Reality: I can't study art. (no puedo — imposible ahora)" },
            { left: "She wishes she could live closer.", right: "Reality: She can't live closer. (circunstancias lo impiden)" }
          ],
          a: "completed",
          successTip: "WISH + COULD = present impossible ability! 'I wish I could' = I CAN'T (right now). Could is the past form of can — it signals unreality after wish! Different from 'I hope I can' (still possible!). 💭"
        },

        {
          type: "mc",
          q: "Marcus's reality: 'I can't study art — my company won't pay for retraining.' His wish:",
          options: [
            "He wishes he could study art.",
            "He wishes he can study art.",
            "He wishes he studied art."
          ],
          a: "He wishes he could study art.",
          tip: "Impossible ability (can't) → wish + could. Can ❌ (never present after wish!). Studied = present wish about current situation (different meaning — he doesn't study now).",
          successTip: "COULD STUDY ART ✅ — impossible ability! CAN ❌ (never present after wish!). STUDIED ≠ COULD STUDY — 'wish I studied' = I don't study (present situation). 'Wish I could study' = I'm unable to. Subtle but important! 🎨"
        },

        {
          type: "mc",
          q: "Nadia's reality: 'I can't live closer to Morocco — my job is in London.' Her wish:",
          options: [
            "She wishes she could live closer to Morocco.",
            "She wishes she can live closer to Morocco.",
            "She wishes she lived closer to Morocco."
          ],
          a: "She wishes she could live closer to Morocco.",
          tip: "Both 'could live' (impossible ability) and 'lived' (present wish) are grammatically correct but have different nuances. Could = she's unable to. Lived = she doesn't (but might be able to if she chose to).",
          successTip: "COULD LIVE (impossible — her job makes it impossible) vs LIVED (present wish — she doesn't but could theoretically choose to). Subtle difference! LIVE FAR AWAY vocabulary! 🌍"
        },

        {
          type: "mc",
          q: "Lena's reality: 'I can't decide what to study — everything interests me.' Her wish:",
          options: [
            "She wishes she could decide what to study.",
            "She wishes she could have decided what to study.",
            "She wishes she decided what to study."
          ],
          a: "She wishes she could decide what to study.",
          tip: "Present inability → wish + could. Could have decided = past perfect of could (past regret about an ability — wrong here, she's 19 and deciding NOW). Decided = present wish (slightly different meaning).",
          successTip: "COULD DECIDE ✅ — present inability (she's currently unable to decide). COULD HAVE DECIDED ❌ = she had the ability in the past but didn't use it. Different structure for different time! TOO YOUNG theme! 👶"
        },

        {
          type: "mc",
          q: "Dani's reality: 'I can't go back and undo giving up that project.' His wish:",
          options: [
            "He wishes he could go back and undo it.",
            "He wishes he could have gone back and undone it.",
            "He wishes he could went back."
          ],
          a: "He wishes he could go back and undo it.",
          tip: "Present impossible desire (can't go back now) → wish + could + base verb. Could have gone = past wish about past ability. Could went ❌ (never past after could in base structure).",
          successTip: "COULD GO BACK ✅ — present impossible desire (you can never go back in time!). COULD HAVE GONE ❌ = past ability he didn't use. Going back in time is ALWAYS impossible, so COULD (present) is correct! GIVE UP! 📚"
        },

        {
          type: "mc",
          q: "Elena: 'I wish I ___ drop out of school — it was the only option at the time.' vs 'I wish I ___ go back and study art properly.'",
          options: [
            "couldn't have / could",
            "could / could",
            "hadn't had to / could"
          ],
          a: "couldn't have / could",
          tip: "First: she wishes the past situation had been impossible to drop out (past regret, complex). Second: she wishes she could go back (present impossible). Could = present wish about ability.",
          successTip: "COULDN'T HAVE (past — she wishes dropping out had been impossible) + COULD (present — she wishes she could go back now). Complex contrast! DROP OUT + STUDY ART! 🎨"
        },

        {
          type: "mc",
          q: "The facilitator asks: 'Who wishes they could change something about their life RIGHT NOW?' Choose the correct wish for each person: Nadia (___ live closer), Marcus (___ study art), Lena (___ know what she wants).",
          options: [
            "could live / could study / could know",
            "could live / could study / could knew",
            "can live / can study / can know"
          ],
          a: "could live / could study / could know",
          tip: "Three present impossible wishes = three wish + could. Base verb after could (live/study/know — not lived/studied/knew!).",
          successTip: "COULD LIVE + COULD STUDY + COULD KNOW — three wish + could structures! Base verb after COULD (not past form!). LIVE FAR AWAY + STUDY ART + TOO YOUNG theme! 🌍🎨👶"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá pudiera estudiar arte — y ojalá pudiera vivir más cerca de mi familia al mismo tiempo.'",
          words: ["I", "wish", "I", "could", "study", "art", "—", "and", "I", "wish", "I", "could", "live", "closer", "to", "my", "family", "at", "the", "same", "time."],
          a: "I wish I could study art — and I wish I could live closer to my family at the same time.",
          extra: ["can", "studied", "lived"]
        },

        {
          type: "input",
          q: "Marcus's deepest wish: '___ ___ ___ ___ art — not just dream ___ it.' (I wish / could / study / of)",
          a: "I / wish / I / could / study / of",
          alts: ["I wish I could study art / dream of it"],
          successTip: "I WISH I COULD STUDY ART — NOT JUST DREAM OF IT. COULD STUDY (present impossible ability) + DREAM OF (collocation from Unit 13!). The units connect! 🎨💭✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: IF ONLY (B2)
    // ===================================================================
    {
      id: "m10-u15-set7",
      title: "Set 7: If Only...",
      context: `*The workshop — emotional peak*

By mid-afternoon, the sentences in the room had weight.
People were writing things they had never said aloud.
The facilitator introduced a new phrase.
"When wish isn't strong enough," he said,
"there's another way. Two words.
If only."
He paused. "Say it out loud. Feel the difference."

*El taller — el momento más emotivo*

A media tarde, las frases en la sala tenían peso.
La gente escribía cosas que nunca había dicho en voz alta.
El facilitador introdujo una nueva frase.
"Cuando wish no es suficientemente fuerte," dijo,
"hay otra manera. Dos palabras.
If only."
Hizo una pausa. "Decidlo en voz alta. Sentid la diferencia."`,

      questions: [
        {
          type: "match",
          q: "Match if only to its rules and contrast with wish:",
          pairs: [
            { left: "if only + past simple", right: "presente irreal — más enfático que wish. 'If only I lived closer!'" },
            { left: "if only + past perfect", right: "pasado irreal — más enfático. 'If only I hadn't dropped out!'" },
            { left: "emotion", right: "If only = stronger regret/longing — often used with ! or spoken with feeling" }
          ],
          a: "completed",
          successTip: "IF ONLY = same grammar as WISH but MORE emphatic/emotional! Same structures: if only + past simple (present) / if only + past perfect (past). The difference is INTENSITY, not grammar! 💔"
        },

        {
          type: "mc",
          q: "Transform: 'I wish I hadn't married so young.' → More emphatic:",
          options: [
            "If only I hadn't married so young!",
            "If only I didn't marry so young!",
            "If only I married later!"
          ],
          a: "If only I hadn't married so young!",
          tip: "If only + past perfect (same as wish + past perfect). Hadn't married = past regret. The exclamation mark signals the stronger emotion!",
          successTip: "IF ONLY I HADN'T MARRIED SO YOUNG! — same structure as wish, stronger emotion. Past perfect (hadn't married) = past regret. The ! is part of the feeling! MARRY vocabulary! 💍💔"
        },

        {
          type: "mc",
          q: "Transform: 'I wish I didn't live so far away.' → More emphatic:",
          options: [
            "If only I didn't live so far away!",
            "If only I hadn't lived so far away!",
            "If only I could live closer!"
          ],
          a: "If only I didn't live so far away!",
          tip: "Present wish → if only + past simple. Didn't live = same structure. Hadn't lived = would be past regret (different meaning).",
          successTip: "IF ONLY I DIDN'T LIVE SO FAR AWAY! — same structure as wish + past simple, stronger emotion. LIVE FAR AWAY vocabulary! Hadn't lived ❌ = that's past regret, not present wish! 🌍💔"
        },

        {
          type: "mc",
          q: "Elena writes: '___ ___ ___ ___ art!' The facilitator tells her it's the most powerful sentence in the room.",
          options: ["If only / I / had / studied", "I wish / I / had / studied", "If only / I / studied / art"],
          a: "If only / I / had / studied",
          tip: "If only + past perfect (had studied) = past regret, emphatic. Studied = past simple (present wish). The exclamation makes if only the stronger choice here.",
          successTip: "IF ONLY I HAD STUDIED ART! — past perfect (past regret) + if only (emphatic) + ! (emotional). The most powerful form of the regret. STUDY ART vocabulary! 🎨💔"
        },

        {
          type: "mc",
          q: "Choose the sentence where IF ONLY is used INCORRECTLY:",
          options: [
            "If only I could study art — everything would be different.",
            "If only I will study art someday!",
            "If only I hadn't given up when I was younger!"
          ],
          a: "If only I will study art someday!",
          tip: "WILL after if only = impossible! Like wish, if only is followed by past simple, past perfect, or could. Never will/would/present!",
          successTip: "IF ONLY + WILL ❌ — never future after if only! Just like wish, if only takes past simple (present unreal), past perfect (past regret), or could (impossible ability). NEVER will! 🚫"
        },

        {
          type: "mc",
          q: "Dani reads his sentence aloud: 'If only I hadn't given up — if only I had believed in myself.' The group is moved. Why is this more powerful than 'I wish'?",
          options: [
            "If only carries more emotional weight and urgency than wish — same grammar, stronger feeling.",
            "If only uses different grammar — past perfect is only possible with if only.",
            "If only is more formal than wish — used in written English only."
          ],
          a: "If only carries more emotional weight and urgency than wish — same grammar, stronger feeling.",
          tip: "If only = same grammar as wish, more emotional. It can be used in both speech and writing.",
          successTip: "IF ONLY = WISH + more emotion! Same structures (past simple / past perfect / could), stronger feeling. HADN'T GIVEN UP = past regret. Dani's sentence works because of that raw emotion! 📚💔"
        },

        {
          type: "mc",
          q: "Lena reads hers: '___ I ___ older — ___ I ___ what to do with my life!' Which combination works?",
          options: [
            "If only / were / if only / knew",
            "I wish / was / if only / knew",
            "Both A and B are correct — wish and if only can be mixed.",
          ],
          a: "Both A and B are correct — wish and if only can be mixed.",
          tip: "Wish and if only can both be used — and can be mixed in the same passage. Were (formal) / was (informal) both correct.",
          successTip: "BOTH CORRECT! Wish and if only can be mixed — same grammar, different intensity. WERE (formal) or WAS (informal) both accepted. Lena's double present wish — TOO YOUNG theme! 👶💔"
        },

        {
          type: "order",
          q: "Reorder: '¡Ojalá no me hubiera rendido! ¡Ojalá hubiera estudiado arte en lugar de rendirme!'",
          words: ["If", "only", "I", "hadn't", "given", "up!", "If", "only", "I", "had", "studied", "art", "instead", "of", "giving", "up!"],
          a: "If only I hadn't given up! If only I had studied art instead of giving up!",
          extra: ["have", "gave", "studied"]
        },

        {
          type: "mc",
          q: "The facilitator says: 'Now rewrite your wish sentence using if only — and feel what changes.' Marcus had written: 'I wish I had studied art.' His if only version:",
          options: [
            "If only I had studied art!",
            "If only I studied art!",
            "If only I could have studied art!"
          ],
          a: "If only I had studied art!",
          tip: "Same structure as the wish: had studied (past perfect for past regret). If only + ! for emotion.",
          successTip: "IF ONLY I HAD STUDIED ART! — exact same structure as the wish, with more emotional weight. The exclamation mark is key — it shows the intensity! STUDY ART! 🎨💔"
        },

        {
          type: "input",
          q: "Priya's emotional moment: '___ ___ ___ ___ so young — ___ ___ ___ ___ more time.' (If only + hadn't married / had taken)",
          a: "If / only / I / hadn't / married / if / only / I / had / taken",
          alts: ["If only I hadn't married so young / If only I had taken more time"],
          successTip: "IF ONLY I HADN'T MARRIED SO YOUNG + IF ONLY I HAD TAKEN MORE TIME — two past perfect regrets, both if only for maximum emotional impact. MARRY vocabulary! 💍💔✅"
        }
      ]
    },

    // ===================================================================
    // SET 8: SEQUENCES OF WISHES AND REGRETS (B2+)
    // ===================================================================
    {
      id: "m10-u15-set8",
      title: "Set 8: A Life in Wishes",
      context: `*The workshop — sharing circle*

At five o'clock, the facilitator asked each person
to read everything they had written.
Not just one sentence — all of them.
Present wishes. Past regrets. Things they couldn't do.
Things they wished they hadn't done.
And somewhere in all of it —
the life they were actually living.

*El taller — círculo de lectura*

A las cinco, el facilitador pidió a cada persona
que leyera todo lo que había escrito.
No solo una frase — todas.
Deseos presentes. Lamentos pasados. Cosas que no podían hacer.
Cosas que deseaban no haber hecho.
Y en algún lugar de todo ello —
la vida que realmente estaban viviendo.`,

      questions: [
        {
          type: "mc",
          q: "Elena reads her sequence. Choose the correct forms: 'I wish I ___ in art right now. I wish I ___ out of school. If only I ___ art properly — everything would be different.'",
          options: [
            "worked / hadn't dropped / had studied",
            "had worked / didn't drop / studied",
            "work / haven't dropped / have studied"
          ],
          a: "worked / hadn't dropped / had studied",
          tip: "worked = present wish (she doesn't work in art NOW). Hadn't dropped = past regret (she dropped out). Had studied = past regret if only (she didn't study art properly).",
          successTip: "WORKED (present wish) + HADN'T DROPPED OUT (past regret) + HAD STUDIED (if only + past regret). Three structures, one life story! DROP OUT + STUDY ART! 🎨"
        },

        {
          type: "mc",
          q: "Dani reads: 'I wish I ___ up so easily — it's a habit I ___ I ___ sooner. And I wish I ___ ___ on my first project.'",
          options: [
            "didn't give / wish / had recognised / hadn't given up",
            "hadn't given / hope / recognised / didn't give up",
            "give / wished / recognise / hadn't given"
          ],
          a: "didn't give / wish / had recognised / hadn't given up",
          tip: "Current habit: didn't give up. Nested wish about recognising: wish + had recognised (past perfect). Past specific event: hadn't given up.",
          successTip: "DIDN'T GIVE UP (present habit wish) + WISH + HAD RECOGNISED (nested past regret!) + HADN'T GIVEN UP (specific past event). Three structures + GIVE UP vocabulary! 📚"
        },

        {
          type: "mc",
          q: "Nadia reads: 'I wish I ___ so far away. I wish I ___ closer when my mother was ill. If only I ___ — but I couldn't afford to.'",
          options: [
            "didn't live / had lived / could have moved",
            "hadn't lived / lived / could move",
            "don't live / have lived / can move"
          ],
          a: "didn't live / had lived / could have moved",
          tip: "Current distance: didn't live (present wish). Past specific time: had lived (past regret — when mother was ill). Impossible past ability: could have moved (past form of could = impossible past ability).",
          successTip: "DIDN'T LIVE (present) + HAD LIVED (past regret) + COULD HAVE MOVED (impossible past ability). Three structures! LIVE FAR AWAY vocabulary across all three! 🌍💔"
        },

        {
          type: "mc",
          q: "Marcus reads: 'I wish I ___ in finance. If only I ___ art at university. I wish I ___ it — but I ___ I still ___.'",
          options: [
            "didn't work / had studied / could change / hope / can",
            "hadn't worked / studied / could change / wish / could",
            "don't work / have studied / changed / hope / do"
          ],
          a: "didn't work / had studied / could change / hope / can",
          tip: "Didn't work = present wish. Had studied = past regret. Could change = present impossible wish. Hope + can = real future possibility (not wish!).",
          successTip: "DIDN'T WORK (present) + HAD STUDIED (past regret) + COULD CHANGE (impossible wish) + HOPE + CAN (real future possibility!). Four structures including the wish vs hope contrast! 🎨"
        },

        {
          type: "mc",
          q: "Priya reads the hardest sentence: 'I wish I ___ married so young. I wish I ___ more time — to know myself before ___ someone else. If only I ___.'",
          options: [
            "hadn't / had taken / knowing / had waited",
            "didn't / took / to know / waited",
            "haven't / have taken / to know / would have waited"
          ],
          a: "hadn't / had taken / knowing / had waited",
          tip: "Past regret: hadn't married. Past regret: had taken more time. Gerund after preposition: knowing. If only + past perfect: had waited.",
          successTip: "HADN'T MARRIED (past regret) + HAD TAKEN (past regret) + KNOWING (gerund after before!) + HAD WAITED (if only + past perfect). MARRY vocabulary throughout! 💍💔"
        },

        {
          type: "mc",
          q: "Lena (19) reads last: 'I wish I ___ what I wanted. I ___ I ___ give up like some people do. And if only I ___ older — just for a moment — to see how it all turns out.'",
          options: [
            "knew / hope / don't / were",
            "had known / wish / hadn't given / was",
            "know / hope / didn't / am"
          ],
          a: "knew / hope / don't / were",
          tip: "Present wish: knew. Hope + don't = real future possibility (she hasn't given up yet!). If only + were = emphatic present wish (too young theme).",
          successTip: "KNEW (present wish — she doesn't know) + HOPE + DON'T (real possibility!) + IF ONLY + WERE (emphatic present wish — TOO YOUNG!). The youngest person uses hope — she still has time! 👶🌟"
        },

        {
          type: "order",
          q: "Reorder Elena's complete wish sequence: 'Ojalá trabajara en arte. Ojalá no hubiera abandonado la escuela. Si tan solo hubiera estudiado arte de verdad — todo sería diferente.'",
          words: ["I", "wish", "I", "worked", "in", "art.", "I", "wish", "I", "hadn't", "dropped", "out.", "If", "only", "I", "had", "studied", "art", "properly", "—", "everything", "would", "be", "different."],
          a: "I wish I worked in art. I wish I hadn't dropped out. If only I had studied art properly — everything would be different.",
          extra: ["have", "hadn't", "worked"]
        },

        {
          type: "mc",
          q: "The facilitator notes: 'Lena is the only one who used HOPE. Why is that significant?'",
          options: [
            "Because she's 19 — her life is still ahead of her. HOPE = real possibility. The others are older and some doors feel closed — WISH = unreal.",
            "Because hope is always more correct than wish grammatically.",
            "Because hope is used for past events, wish for present ones."
          ],
          a: "Because she's 19 — her life is still ahead of her. HOPE = real possibility. The others are older and some doors feel closed — WISH = unreal.",
          tip: "Hope = still possible. Wish = unreal/impossible. Age and life stage determine which one fits!",
          successTip: "HOPE (real possibility) vs WISH (unreal). Lena at 19 = still possible = HOPE. The others have made choices they can't fully undo = WISH. The grammar reflects the emotional reality! 🌟💔"
        },

        {
          type: "input",
          q: "Write the group's collective sentence: 'We all ___ ___ ___ done things differently — but we ___ we ___ still change what comes next.' (wish / had / hope / can)",
          a: "wish / we / had / hope / we / can",
          alts: ["wish we had done things differently / hope we can change"],
          successTip: "WISH WE HAD (collective past regret) + HOPE WE CAN (collective real future possibility). The whole room united — regret behind, possibility ahead. WISH vs HOPE! 🌟💔✅"
        }
      ]
    },

    // ===================================================================
    // SET 9: WISH vs HOPE (B2+)
    // ===================================================================
    {
      id: "m10-u15-set9",
      title: "Set 9: Still Possible or Already Gone?",
      context: `*The workshop — final theory session*

"Before we finish," said the facilitator,
"I want to talk about the difference between a wish and a hope.
A wish is for something that isn't real — and may never be.
A hope is for something that isn't real yet — but could be.
The grammar tells you which one you mean.
And which one you believe."

*El taller — última sesión teórica*

"Antes de terminar," dijo el facilitador,
"quiero hablar de la diferencia entre un deseo y una esperanza.
Un deseo es para algo que no es real — y que quizás nunca lo será.
Una esperanza es para algo que todavía no es real — pero podría serlo.
La gramática te dice cuál de los dos quieres decir.
Y cuál de los dos crees."`,

      questions: [
        {
          type: "match",
          q: "Match wish and hope to their grammar and meaning:",
          pairs: [
            { left: "wish + past tense", right: "irreal — no es posible ahora o ya no puede cambiar" },
            { left: "hope + present/will", right: "real — todavía es posible, puede ocurrir" },
            { left: "I wish I could study art. vs I hope I can study art someday.",
              right: "Wish = impossible now. Hope = still possible in the future." }
          ],
          a: "completed",
          successTip: "WISH + past = unreal (impossible or unchangeable). HOPE + present/will = real possibility (still can happen!). 'I wish I could' = I can't. 'I hope I can' = I might! The grammar reveals your belief! 🌟💔"
        },

        {
          type: "mc",
          q: "Marcus is 35 and dreams of art. Which sentence means he STILL BELIEVES it's possible?",
          options: [
            "He hopes he can study art before it's too late.",
            "He wishes he could study art.",
            "He wishes he had studied art."
          ],
          a: "He hopes he can study art before it's too late.",
          tip: "Hope + can = real future possibility. Wish + could = currently impossible. Wish + had studied = past regret.",
          successTip: "HOPES HE CAN ✅ — still possible! WISHES HE COULD = impossible now. WISHES HE HAD = past regret (done). Three different beliefs about the same dream! 🎨🌟"
        },

        {
          type: "mc",
          q: "Nadia is considering moving back to Morocco. Which sentence means she thinks it MIGHT happen?",
          options: [
            "She hopes she can live closer to her family one day.",
            "She wishes she didn't live so far away.",
            "She wishes she could live closer."
          ],
          a: "She hopes she can live closer to her family one day.",
          tip: "Hope + can = real possibility (she's considering it). Wish + didn't = current unreal situation. Wish + could = currently impossible.",
          successTip: "HOPES SHE CAN ✅ — she's thinking about it, it could happen! WISHES SHE DIDN'T (unreal NOW) vs HOPES SHE CAN (possible FUTURE). The difference between resignation and possibility! 🌍🌟"
        },

        {
          type: "mc",
          q: "Priya's marriage: 'She ___ she ___ married him. But she ___ she ___ find happiness again.'",
          options: ["wishes / hadn't / hopes / can", "hopes / hadn't / wishes / can", "wishes / didn't / hopes / will"],
          a: "wishes / hadn't / hopes / can",
          tip: "Wishes + hadn't married = past regret (can't undo). Hopes + can = real future possibility (happiness is still possible!).",
          successTip: "WISHES + HADN'T (past regret — the marriage happened) + HOPES + CAN (real future possibility — happiness is still ahead!). Regret behind, hope ahead. MARRY vocabulary! 💍🌟"
        },

        {
          type: "mc",
          q: "Elena says two things. Which one means she has given up on studying art?",
          options: [
            "'I wish I could study art.' (impossible — she's given up on it being possible now)",
            "'I hope I can study art someday.' (possible — she hasn't given up)",
            "'I wish I had studied art.' (past regret — about the past, not about now)"
          ],
          a: "'I wish I could study art.' (impossible — she's given up on it being possible now)",
          tip: "Wish + could = currently impossible (she believes she can't). Hope + can = still possible (she believes she might). The grammar reveals her level of belief.",
          successTip: "WISH + COULD = currently impossible (she's accepted it won't happen now). HOPE + CAN = still possible (she believes). WISH + HAD = past regret (about the past, not future). Three very different sentences! 🎨🌟💔"
        },

        {
          type: "mc",
          q: "Lena says: 'I ___ I ___ what I want to do — but I also ___ that I ___ it out eventually.' Which pair shows she hasn't given up?",
          options: ["wish / knew / hope / figure", "wish / knew / hope / will figure", "if only / knew / hope / figured"],
          a: "wish / knew / hope / will figure",
          tip: "Wish + knew = current unreal (she doesn't know now). Hope + will figure = real future possibility (she believes she'll work it out!). Will figure = confident future.",
          successTip: "WISH + KNEW (unreal NOW — she doesn't know) + HOPE + WILL FIGURE OUT (real future confidence!). The combination: honest about now, hopeful about the future. TOO YOUNG theme! 👶🌟"
        },

        {
          type: "mc",
          q: "Dani says: 'I wish I ___ given up — but I ___ I ___ give up on the new project I'm starting.' Which pair is correct?",
          options: ["hadn't / hope / don't", "didn't / hope / won't", "hadn't / wish / don't"],
          a: "hadn't / hope / don't",
          tip: "Hadn't given up = past regret (past perfect). Hope + don't = real future possibility (he can choose not to give up!).",
          successTip: "HADN'T GIVEN UP (past regret) + HOPE + DON'T GIVE UP (real future choice — he can decide!). The past is fixed, the future isn't. GIVE UP vocabulary! 📚🌟"
        },

        {
          type: "order",
          q: "Reorder: 'Ojalá hubiera estudiado arte — pero espero que todavía pueda encontrar la manera de hacerlo parte de mi vida.'",
          words: ["I", "wish", "I", "had", "studied", "art", "—", "but", "I", "hope", "I", "can", "still", "find", "a", "way", "to", "make", "it", "part", "of", "my", "life."],
          a: "I wish I had studied art — but I hope I can still find a way to make it part of my life.",
          extra: ["could", "have", "find"]
        },

        {
          type: "mc",
          q: "The facilitator writes on the board: 'Wish = ___ . Hope = ___.' What belongs in each blank?",
          options: [
            "the life you didn't live / the life you might still live",
            "the past / the future",
            "regret / desire"
          ],
          a: "the life you didn't live / the life you might still live",
          tip: "The facilitator's poetic formulation: wish = what isn't real (past or present). Hope = what could become real (future). Both about the gap between reality and desire — but different gaps.",
          successTip: "WISH = the life you DIDN'T live (unreal — past or present). HOPE = the life you MIGHT STILL live (possible — future). The most elegant way to understand the grammar! 🌟💔✅"
        },

        {
          type: "input",
          q: "Marcus's final sentence of the day: '___ ___ ___ studied art — but ___ ___ still ___ a way.' (I wish / had / I hope / I / find — past regret + future hope)",
          a: "I / wish / I / had / I / hope / I / can / find",
          alts: ["I wish I had studied art / I hope I can still find a way"],
          successTip: "I WISH I HAD STUDIED ART + I HOPE I CAN STILL FIND A WAY — past regret + future possibility. STUDY ART vocabulary! The full arc of a wish: from regret to hope. ✅🎨🌟"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL NARRATIVE — THE FINAL UNIT (B2+)
    // ===================================================================
    {
      id: "m10-u15-set10",
      title: "Set 10: The Last Page",
      context: `*London — the workshop ends. All characters.*

The workshop was over.
Six people. Six lives. Six notebooks full of wishes and regrets.
But as they left the building into the London evening,
something had shifted.
They had said the things they had never said.
They had written the lives they had not lived.
And somehow — saying it had made the actual life feel more possible.

*Londres — termina el taller. Todos los personajes.*

El taller había terminado.
Seis personas. Seis vidas. Seis cuadernos llenos de deseos y lamentos.
Pero mientras salían del edificio al atardecer londinense,
algo había cambiado.
Habían dicho las cosas que nunca habían dicho.
Habían escrito las vidas que no habían vivido.
Y de alguna manera — decirlo había hecho que la vida real pareciera más posible.`,

      questions: [
        {
          type: "mc",
          q: "Elena's final notebook sentence (past regret): '___ ___ ___ ___ of school — but ___ ___ ___ still ___ art professionally.'",
          options: ["I wish / I / hadn't / dropped out / I hope / I / can / practise", "I wish / I / didn't / drop out / I hope / I / could / study", "If only / I / hadn't / dropped / I wish / I / can / study"],
          a: "I wish / I / hadn't / dropped out / I hope / I / can / practise",
          tip: "Past regret: hadn't dropped out. Future hope: hope + can. DROP OUT vocabulary.",
          successTip: "I WISH I HADN'T DROPPED OUT + I HOPE I CAN STILL PRACTISE ART — past regret + future hope. DROP OUT + STUDY ART! Elena's full arc. 🎨💔🌟"
        },

        {
          type: "mc",
          q: "Dani's two sentences (present + past): '___ ___ up so easily — and ___ ___ on that project in 2019.'",
          options: ["I wish I didn't give / I wish I hadn't given up", "I wish I hadn't given / I wish I didn't give up", "If only I don't give / if only I hadn't given"],
          a: "I wish I didn't give / I wish I hadn't given up",
          tip: "Current habit: didn't give up (past simple). Specific past event: hadn't given up (past perfect). GIVE UP vocabulary twice.",
          successTip: "DIDN'T GIVE UP (current habit wish) + HADN'T GIVEN UP (past specific event). Both GIVE UP — different time frames, different structures! 📚🔑"
        },

        {
          type: "mc",
          q: "Nadia's three-sentence sequence: '___ ___ so far away. ___ ___ closer when my mother needed me. ___ ___ visit more often.'",
          options: ["I wish I didn't live / I wish I had lived / I hope I can", "I wish I hadn't lived / I wish I lived / I hope I could", "If only I don't live / if only I lived / I wish I can"],
          a: "I wish I didn't live / I wish I had lived / I hope I can",
          tip: "Current: didn't live. Past specific: had lived. Future hope: hope + can. LIVE FAR AWAY throughout.",
          successTip: "DIDN'T LIVE (present) + HAD LIVED (past regret) + HOPE + CAN (future!). Three time frames in three sentences. LIVE FAR AWAY vocabulary across all three! 🌍💔🌟"
        },

        {
          type: "mc",
          q: "Marcus's emotional finale: '___ ___ ___ — ___ ___ art. ___ ___ finance. But ___ ___ still ___ the life I want.'",
          options: [
            "If only / I / had studied / if only / I / didn't work / I / hope / I / can / build",
            "I wish / I / studied / I wish / I / hadn't worked / I / hope / I / could / build",
            "If only / I / studied / if only / I / didn't work / I / hope / I / will"
          ],
          a: "If only / I / had studied / if only / I / didn't work / I / hope / I / can / build",
          tip: "If only + had studied = past regret emphatic. If only + didn't work = present wish emphatic. Hope + can = real future.",
          successTip: "IF ONLY + HAD STUDIED (emphatic past regret) + IF ONLY + DIDN'T WORK (emphatic present wish) + HOPE + CAN BUILD (real future!). STUDY ART vocabulary! Marcus's full story! 🎨💔🌟"
        },

        {
          type: "mc",
          q: "Priya's hardest sentence: '___ ___ so young — ___ ___ known myself first. ___ ___ find happiness again — on my own terms.'",
          options: [
            "I wish I hadn't married / I wish I had / I hope I can",
            "If only I didn't marry / if only I knew / I hope I will",
            "I wish I hadn't married / if only I had / I hope I can"
          ],
          a: "I wish I hadn't married / if only I had / I hope I can",
          tip: "Hadn't married = past regret. If only I had known = emphatic past regret. Hope + can = real future. MARRY vocabulary.",
          successTip: "HADN'T MARRIED (past regret) + IF ONLY I HAD KNOWN (emphatic!) + HOPE + CAN (future). MARRY vocabulary! Priya's story ends with possibility. 💍💔🌟"
        },

        {
          type: "mc",
          q: "Lena's last sentence — the youngest, the most hopeful: '___ ___ what I want — but ___ I ___ out. ___ ___ ___ older — just to see.'",
          options: [
            "I wish I knew / I hope / figure / if only / I / were",
            "I wish I had known / I hope / figured / if only / I / was",
            "I wish I knew / I hope / will figure / if only / I / were"
          ],
          a: "I wish I knew / I hope / will figure / if only / I / were",
          tip: "Present wish: knew. Future hope: will figure out. If only + were = emphatic present wish (too young). All three structures.",
          successTip: "KNEW (present wish) + WILL FIGURE OUT (future hope!) + IF ONLY + WERE (emphatic present wish). TOO YOUNG theme! Lena ends the unit with hope — she's 19! 👶🌟"
        },

        {
          type: "mc",
          q: "As they leave, all six say the same thing in different words. What structure do they use when they think about tomorrow?",
          options: [
            "Hope + present/will — because tomorrow is still possible for all of them.",
            "Wish + past perfect — because the past is what matters most.",
            "If only — because the regrets are strongest at the end.",
          ],
          a: "Hope + present/will — because tomorrow is still possible for all of them.",
          tip: "Hope = real possibility. The workshop ended with possibility — not just regret. Hope + present/will for all of them.",
          successTip: "HOPE + CAN/WILL — because tomorrow hasn't happened yet. The workshop's message: regret is honest, but hope is also honest. Both are true. Both matter. 🌟"
        },

        {
          type: "order",
          q: "Reorder the final group sentence: 'Ojalá hubiéramos vivido de otra manera — pero esperamos que todavía podamos.'",
          words: ["We", "wish", "we", "had", "lived", "differently", "—", "but", "we", "hope", "we", "still", "can."],
          a: "We wish we had lived differently — but we hope we still can.",
          extra: ["could", "have", "lived"]
        },

        {
          type: "mc",
          q: "The facilitator's final words: 'A wish is not a failure. It's proof that you ___ something — and that you ___ it could be different.' Which words complete this?",
          options: ["wanted / believed", "missed / wished", "lost / hoped"],
          a: "wanted / believed",
          tip: "A wish = you wanted something (desired it) and believed it could be different (imagined another reality). The grammar of wishes is the grammar of human longing.",
          successTip: "WANTED + BELIEVED — the emotional core of every wish in this unit. Every structure (wish + past simple, past perfect, could, if only) expresses the same thing: wanting something different and believing another life was possible. 💭🌟"
        },

        {
          type: "input",
          q: "The very last sentence of the app: '___ ___ ___ studied art, married later, ___ up less, lived ___ — but I ___ I ___ still ___ the person I want to be.' (I wish / had / given / closer / hope / can / become)",
          a: "I / wish / I / had / given / closer / hope / I / can / become",
          alts: ["I wish I had studied art / married later / given up less / lived closer / I hope I can become"],
          successTip: "🏆 THE APP IS COMPLETE! I WISH I HAD (past regrets) × 4 + I HOPE I CAN (future possibility) × 1. All six vocabulary items: study art ✅ marry ✅ give up ✅ live far away ✅ drop out ✅ too young ✅. All structures: wish + past simple ✅ wish + past perfect ✅ wish + could ✅ if only ✅ wish vs hope ✅. Every unit. Every character. Every wish. And one final hope. 🎨💍📚🌍👶🌟"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2+)
  // ===================================================================
  readingSet: {
    id: "m10-u15-reading",
    title: "Reading: Six Notebooks",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Elena's Notebook**

Elena had written three sentences. The first: "I wish I worked in art." The second: "I wish I hadn't dropped out of school at twenty." The third was the hardest: "If only I had studied art properly — I would be a completely different person now." She read them aloud and felt, for the first time, that saying them had made them smaller.`
      },
      {
        type: "mc",
        q: "What do Elena's three sentences show about her wishes?",
        options: [
          "A present wish, a past regret, and an emphatic past regret using if only.",
          "Three past regrets about different events.",
          "Two present wishes and one future hope."
        ],
        a: "A present wish, a past regret, and an emphatic past regret using if only.",
        tip: "'I wish I worked' = present wish. 'I wish I hadn't dropped out' = past regret. 'If only I had studied' = emphatic past regret."
      },
      {
        type: "mc",
        q: "Why did saying the sentences make them feel smaller?",
        options: [
          "Naming a wish or regret out loud reduces its power over you.",
          "The sentences were grammatically incorrect.",
          "Elena had decided to go back to school."
        ],
        a: "Naming a wish or regret out loud reduces its power over you.",
        tip: "'Saying them had made them smaller' — the act of articulating a wish or regret brings relief."
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**Marcus's Turn**

Marcus read slowly. "I wish I didn't work in finance. I wish I had studied art. If only I had believed in myself at eighteen." He paused. Then he added one more sentence — one he hadn't written down: "But I hope I still can." The room was quiet. Lena started to cry.`
      },
      {
        type: "mc",
        q: "What is the grammatical difference between Marcus's first three sentences and his final one?",
        options: [
          "The first three use wish/if only (unreal). The last uses hope (still possible).",
          "The first three are past regrets. The last is also a past regret.",
          "The first three use past simple. The last uses past perfect."
        ],
        a: "The first three use wish/if only (unreal). The last uses hope (still possible).",
        tip: "Wish/if only = unreal. Hope = still possible. Marcus shifted from regret to possibility with that one final sentence."
      },
      {
        type: "mc",
        q: "Why did Lena start to cry?",
        options: [
          "Because Marcus's hope — spoken aloud — reflected something she felt too.",
          "Because Marcus's grammar was incorrect.",
          "Because she had also studied finance."
        ],
        a: "Because Marcus's hope — spoken aloud — reflected something she felt too.",
        tip: "Lena is 19 and full of hope herself. Marcus's 'I hope I still can' resonated with her own feeling that the future is still open."
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**Priya and Dani**

Priya read quietly: "I wish I hadn't married so young. I wish I had known myself first." Dani read next: "I wish I didn't give up so easily — it's a habit I've had my whole life. But I wish I hadn't given up that one project. That one I'll always regret." Two people. Two kinds of regret. The same grammar.`
      },
      {
        type: "mc",
        q: "What is the difference between Dani's two wishes?",
        options: [
          "'Didn't give up' = present wish about a current habit. 'Hadn't given up' = past regret about a specific event.",
          "Both are past regrets about different events.",
          "'Didn't give up' is a future hope. 'Hadn't given up' is a present wish."
        ],
        a: "'Didn't give up' = present wish about a current habit. 'Hadn't given up' = past regret about a specific event.",
        tip: "Didn't give up = past simple = current habit wish. Hadn't given up = past perfect = specific past event regret."
      },
      {
        type: "mc",
        q: "What does the passage mean by 'the same grammar'?",
        options: [
          "Both Priya and Dani used wish + past perfect for past regrets.",
          "Both used if only for emphasis.",
          "Both used hope for future possibility."
        ],
        a: "Both Priya and Dani used wish + past perfect for past regrets.",
        tip: "'Hadn't married' and 'hadn't given up' — both wish + past perfect for completed past events they regret."
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Nadia and Lena**

Nadia read carefully: "I wish I didn't live so far away from my family — and I wish I had been there when my mother needed me." Lena went last. She was the youngest in the room. She read only two sentences: "I wish I knew what I wanted. But I hope I find out." Everyone smiled. She was the only one who had used hope.`
      },
      {
        type: "mc",
        q: "What is the difference between Nadia's two wishes?",
        options: [
          "'Didn't live' = present wish (she lives far away NOW). 'Had been there' = past regret (a specific past moment).",
          "Both are present wishes about her current situation.",
          "'Didn't live' is a past regret. 'Had been there' is a present wish."
        ],
        a: "'Didn't live' = present wish (she lives far away NOW). 'Had been there' = past regret (a specific past moment).",
        tip: "Didn't live = current situation (she's far away right now). Had been there = past event (when her mother was ill)."
      },
      {
        type: "mc",
        q: "Why did everyone smile when Lena used hope?",
        options: [
          "Because hope signals possibility — Lena was the only one who still felt the future was fully open.",
          "Because hope is funnier than wish grammatically.",
          "Because Lena had made a grammar mistake."
        ],
        a: "Because hope signals possibility — Lena was the only one who still felt the future was fully open.",
        tip: "Hope = still possible. At 19, Lena hadn't made the irreversible choices yet — her future was open in a way the others' wasn't."
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Walk Home**

They left the building together and walked along the Thames. Nobody spoke for a while. Then Marcus said: "I wish I had done this years ago — written it all down." Nadia said: "I hope we can do it again." Elena said: "If only we had more time." Lena said nothing. She was already writing in her notebook, walking slowly, smiling at something only she could see.`
      },
      {
        type: "mc",
        q: "Match each person's sentence to its structure: Marcus / Nadia / Elena.",
        options: [
          "Marcus: wish + past perfect (past regret). Nadia: hope + can (real possibility). Elena: if only + past perfect (emphatic past regret).",
          "Marcus: wish + past simple (present wish). Nadia: wish + could (impossible). Elena: if only + past simple (present wish).",
          "All three use wish + past perfect for past regrets."
        ],
        a: "Marcus: wish + past perfect (past regret). Nadia: hope + can (real possibility). Elena: if only + past perfect (emphatic past regret).",
        tip: "'I wish I had done' = past perfect. 'I hope we can' = hope + present. 'If only we had more time' = if only + past perfect."
      },
      {
        type: "mc",
        q: "What does Lena's silence and smile suggest?",
        options: [
          "She has already moved from wish to hope — she's writing the life she wants, not just the one she regrets.",
          "She didn't enjoy the workshop.",
          "She is too young to understand wishes and regrets."
        ],
        a: "She has already moved from wish to hope — she's writing the life she wants, not just the one she regrets.",
        tip: "Writing while walking, smiling — Lena is acting, not just wishing. The workshop's final image: possibility in motion.",
        successTip: "Lena ends the unit — and the app — in motion. Writing. Hoping. Acting. The grammar of wishes tells you where you've been. The grammar of hope tells you where you're going. 🌟✅"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u15-review",
    title: "Unit 15 Review: Wishes, Regrets & Hope",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: 'She lives far away.' → Her present wish:",
        options: ["She wishes she didn't live so far away.", "She wishes she hadn't lived so far away.", "She wishes she doesn't live so far away."],
        a: "She wishes she didn't live so far away.",
        tip: "Current situation → wish + past simple negative. Hadn't = past regret. Doesn't ❌"
      },
      {
        type: "mc",
        q: "Review: 'He dropped out of school at 19.' → His regret:",
        options: ["He wishes he hadn't dropped out.", "He wishes he didn't drop out.", "He wishes he hasn't dropped out."],
        a: "He wishes he hadn't dropped out.",
        tip: "Past event → wish + past perfect negative. Didn't = present wish (wrong). Hasn't ❌"
      },
      {
        type: "mc",
        q: "Review: 'She married too young.' → Her emphatic regret:",
        options: ["If only she hadn't married so young!", "If only she didn't marry so young!", "I wish she married later."],
        a: "If only she hadn't married so young!",
        tip: "Past regret + emphatic = if only + past perfect. Didn't = present wish. If only = same grammar as wish, more emotion!"
      },
      {
        type: "mc",
        q: "Review: 'I can't study art — my job won't allow it.' → Wish:",
        options: ["I wish I could study art.", "I wish I studied art.", "I hope I can study art."],
        a: "I wish I could study art.",
        tip: "Impossible ability (can't) → wish + could. Studied = present wish (different meaning). Hope = real possibility (but he can't!)"
      },
      {
        type: "mc",
        q: "Review: What is the difference between 'I wish I gave up less' and 'I wish I hadn't given up'?",
        options: [
          "'Gave up less' = present wish about a current habit. 'Hadn't given up' = past regret about a specific event.",
          "No difference — both are past regrets.",
          "'Gave up less' is incorrect — should be 'didn't give up'."
        ],
        a: "'Gave up less' = present wish about a current habit. 'Hadn't given up' = past regret about a specific event.",
        tip: "Past simple = current habit wish. Past perfect = specific past event regret."
      },
      {
        type: "mc",
        q: "Review: 'I hope I can study art someday.' vs 'I wish I could study art.' What's the key difference?",
        options: [
          "Hope = still possible (real). Wish + could = currently impossible (unreal).",
          "Hope is more formal than wish.",
          "Both mean the same thing — just different words."
        ],
        a: "Hope = still possible (real). Wish + could = currently impossible (unreal).",
        tip: "Hope + can = real future possibility. Wish + could = currently impossible. The grammar reveals your level of belief!"
      },
      {
        type: "mc",
        q: "Review: Which sentence uses the WRONG tense after wish?",
        options: [
          "I wish I will study art someday.",
          "I wish I had studied art.",
          "I wish I could live closer."
        ],
        a: "I wish I will study art someday.",
        tip: "WILL after wish = impossible! Wish takes past simple (present), past perfect (past), or could (impossible). Never will/future!"
      },
      {
        type: "mc",
        q: "Review: 'She ___ she ___ out of school — but she ___ she ___ still make something of her talent.'",
        options: ["wishes / hadn't dropped / hopes / can", "wishes / didn't drop / hopes / will", "if only / hadn't dropped / wishes / could"],
        a: "wishes / hadn't dropped / hopes / can",
        tip: "Past regret: hadn't dropped out. Future hope: hope + can (still possible!). DROP OUT + STUDY ART theme."
      },
      {
        type: "mc",
        q: "Review: 'He ___ he ___ up on his project in 2019 — and he ___ he ___ up so easily in general.'",
        options: ["wishes / hadn't given / wishes / didn't give", "wishes / didn't give / wishes / hadn't given", "if only / hadn't given / hopes / doesn't give"],
        a: "wishes / hadn't given / wishes / didn't give",
        tip: "2019 = specific past event = hadn't given up (past perfect). In general = current habit = didn't give up (past simple)."
      },
      {
        type: "mc",
        q: "Review: Complete the facilitator's final rule: 'Wish + ___ = present unreal. Wish + ___ = past regret. If only = ___. Hope + ___ = ___.'",
        options: [
          "past simple / past perfect / same as wish but more emphatic / present or will / real possibility",
          "past perfect / past simple / different grammar from wish / past / impossible",
          "could / past simple / only for writing / will / uncertain"
        ],
        a: "past simple / past perfect / same as wish but more emphatic / present or will / real possibility",
        tip: "The complete rule set for the unit!",
        successTip: "🎉 MODULE 10 — UNIT 15 — COMPLETE! 🏆 Wish + past simple (present) ✅ Wish + past perfect (past) ✅ Wish + could (impossible) ✅ If only (emphatic) ✅ Wish vs Hope ✅ drop out · study art · give up · marry · too young · live far away ✅ Six characters. Six lives. One final hope. The app is complete. 🎨💍📚🌍👶🌟"
      }
    ]
  }
};
